// Fast, direct-to-Directus save path for the Jutem Fund ledger grid
// (src/pages/jutemLedgerPage.jsx). Mirrors the borrower-matching and
// loan/amortization/payment logic already used by etl/monthly_update.py,
// but runs in the browser against the live API instead of an external
// Python service — no whole-month review round trip needed for a single row.
import axios from "axios";

const DIRECTUS_URL = "https://zedlenders.pickmesms.com";
export const JUTEM_COMPANY_ID = 22;
const BORROWER_ROLE_ID = "ab3c07d3-a225-4b68-8454-96e00116e307";
const JUTEM_LOAN_PRODUCT_ID = 35; // "Business Micro Loan" (company 22)
const JUTEM_BRANCH_ID = 12; // "Lusaka" (company 22)

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}` };
}

function normalizeName(name) {
  return String(name || "").trim().replace(/\s+/g, " ");
}

function fullName(b) {
  return `${b.first_name || ""} ${b.last_name || ""}`.trim();
}

export async function fetchBorrowerRegistry() {
  const res = await axios.get(`${DIRECTUS_URL}/users`, {
    headers: authHeaders(),
    params: {
      filter: {
        role: { name: { _eq: "Borrower" } },
        company: { _eq: JUTEM_COMPANY_ID },
      },
      fields: "id,first_name,last_name,nrc",
      limit: -1,
    },
  });
  return res.data.data || [];
}

// Same matching order as monthly_update.py's find_borrower(): exact
// case-insensitive name match, then a substring fallback. No fuzzy/
// edit-distance matching — deliberately, to match the existing ETL behavior.
export function matchBorrower(registry, rawName) {
  const name = normalizeName(rawName);
  if (!name) return null;
  const nameLower = name.toLowerCase();

  const exact = registry.find((b) => fullName(b).toLowerCase() === nameLower);
  if (exact) return exact;

  return (
    registry.find((b) => {
      const full = fullName(b).toLowerCase();
      return full && (full.includes(nameLower) || nameLower.includes(full));
    }) || null
  );
}

function splitName(name) {
  const parts = normalizeName(name).split(" ");
  if (parts.length === 1) return { first_name: parts[0], last_name: "N/A" };
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
}

// Placeholder identity fields for a name that only exists in the sheet.
// Uses a random/time-based suffix — deliberately NOT the Python ETL scripts'
// shared incrementing counters, so the two can never collide.
async function createPlaceholderBorrower(rawName) {
  const { first_name, last_name } = splitName(rawName);
  const suffix =
    Date.now().toString().slice(-8) + Math.floor(Math.random() * 90 + 10);
  const payload = {
    first_name,
    last_name,
    email: `jutem${suffix}@kwachaplus.com`,
    phone: `26097${suffix}`.slice(0, 12),
    nrc: `${suffix}/00/1`,
    address: "Lusaka",
    province: "Lusaka",
    employment_type: "self_employed",
    company: JUTEM_COMPANY_ID,
    role: BORROWER_ROLE_ID,
    status: "active",
    password: "JutemBorrower2026!",
  };
  const res = await axios.post(`${DIRECTUS_URL}/users`, payload, {
    headers: authHeaders(),
  });
  return res.data.data;
}

// Resolves a borrower for `rawName`, creating a placeholder if none matches.
// Mutates `registry` in place so later rows in the same save see it too.
export async function resolveBorrower(registry, rawName) {
  const existing = matchBorrower(registry, rawName);
  if (existing) return existing;
  const created = await createPlaceholderBorrower(rawName);
  registry.push(created);
  return created;
}

// Find-or-create the recurring loan for this borrower + amount — the sheet's
// rollover model means the same person borrowing the same amount month after
// month is one ongoing loan, not a new one every time.
export async function findOrCreateLoan(borrowerId, amount) {
  const res = await axios.get(`${DIRECTUS_URL}/items/loans`, {
    headers: authHeaders(),
    params: {
      filter: { borrower: { _eq: borrowerId }, company: { _eq: JUTEM_COMPANY_ID } },
      fields: "id,amount,loan_status",
      limit: -1,
    },
  });
  const loans = res.data.data || [];
  const match = loans.find((l) => Math.abs(Number(l.amount) - Number(amount)) < 1);
  if (match) return match;

  const created = await axios.post(
    `${DIRECTUS_URL}/items/loans`,
    {
      borrower: borrowerId,
      company: JUTEM_COMPANY_ID,
      loan_product: JUTEM_LOAN_PRODUCT_ID,
      branch: JUTEM_BRANCH_ID,
      amount: Number(amount),
      application_date: new Date().toISOString().slice(0, 10),
      // Real app-wide status code — never the legacy 5/6 the Python ETL
      // scripts use, which is exactly the mismatch two SQL scripts already
      // had to clean up after the fact.
      loan_status: 7,
      status: "published",
      loan_purpose: "Business",
    },
    { headers: authHeaders() }
  );
  return created.data.data;
}

// Upserts the month's amortization row for a loan — idempotent by
// (loan_reference, month), so re-saving the same grid never duplicates.
export async function upsertAmortization({
  loanId,
  borrowerNrc,
  monthLabel,
  dueDate,
  amountDue,
  interestRatePct,
  expectedAmount,
  profit,
  status,
}) {
  const res = await axios.get(`${DIRECTUS_URL}/items/amortization`, {
    headers: authHeaders(),
    params: {
      filter: { loan_reference: { _eq: loanId }, month: { _eq: monthLabel } },
      fields: "id",
      limit: 1,
    },
  });
  const existing = (res.data.data || [])[0];
  const payload = {
    loan_reference: loanId,
    borrower_nrc: borrowerNrc || "",
    month: monthLabel,
    due_date: dueDate,
    amount_due: amountDue,
    interest_rate: interestRatePct,
    expected_amount: expectedAmount,
    profit,
    status,
    sheet: monthLabel,
  };
  if (existing) {
    await axios.patch(`${DIRECTUS_URL}/items/amortization/${existing.id}`, payload, {
      headers: authHeaders(),
    });
    return existing.id;
  }
  const created = await axios.post(`${DIRECTUS_URL}/items/amortization`, payload, {
    headers: authHeaders(),
  });
  return created.data.data.id;
}

// Records a "Loan Repayment" transaction for a Paid row, skipping if one
// already exists for this loan on this due date (re-save safety).
export async function ensurePaymentRecorded({ loanId, amount, dueDate }) {
  const existingRes = await axios.get(`${DIRECTUS_URL}/items/transactions`, {
    headers: authHeaders(),
    params: {
      filter: {
        loan: { _eq: loanId },
        transaction_type: { _eq: "Loan Repayment" },
        transaction_date: { _eq: dueDate },
      },
      fields: "id",
      limit: 1,
    },
  });
  if ((existingRes.data.data || []).length) return;

  const countRes = await axios.get(`${DIRECTUS_URL}/items/transactions`, {
    headers: authHeaders(),
    params: {
      filter: { company: { _eq: JUTEM_COMPANY_ID } },
      aggregate: { count: "id" },
    },
  });
  const seq = Number(countRes.data.data?.[0]?.count?.id || 0) + 1;

  await axios.post(
    `${DIRECTUS_URL}/items/transactions`,
    {
      amount: Number(amount),
      transaction_date: dueDate,
      is_loan_transaction: true,
      loan: loanId,
      transaction_type: "Loan Repayment",
      transaction_status: "Completed",
      payment_method: "cash",
      reference_number: `PMT-${String(seq).padStart(5, "0")}`,
      company: JUTEM_COMPANY_ID,
    },
    { headers: authHeaders() }
  );
}

// The sheet's DATE EXPECTED column is a day-of-month ordinal like "25th" —
// combine it with the selected month to get a real ISO date.
function ordinalToISODate(monthMoment, ordinalText) {
  const match = String(ordinalText || "").match(/\d+/);
  const day = match ? parseInt(match[0], 10) : monthMoment.date();
  return monthMoment.clone().date(day).format("YYYY-MM-DD");
}

const num = (x) => parseFloat(String(x ?? "").replace(/,/g, "")) || 0;

// Saves every row in the grid that has a NAME and AMOUNT LOANED. Returns a
// summary the page can show the operator. One row's failure doesn't stop
// the rest — errors are collected and reported.
export async function saveLedgerMonth(rows, monthMoment) {
  const registry = await fetchBorrowerRegistry();
  const monthLabel = monthMoment.format("MMMM YYYY").toUpperCase();
  const result = { saved: 0, newBorrowers: 0, paymentsRecorded: 0, errors: [] };

  for (const row of rows) {
    const [name, amountLoanedRaw, interestRaw, expectedRaw, dateExpected, profitRaw, status, ] = row;
    if (!normalizeName(name) || !num(amountLoanedRaw)) continue;

    try {
      const beforeCount = registry.length;
      const borrower = await resolveBorrower(registry, name);
      if (registry.length > beforeCount) result.newBorrowers += 1;

      const amountLoaned = num(amountLoanedRaw);
      const interestRatePct = num(interestRaw);
      const expectedAmount = num(expectedRaw) || amountLoaned * (1 + interestRatePct / 100);
      const profit = num(profitRaw) || amountLoaned * (interestRatePct / 100);
      const dueDate = ordinalToISODate(monthMoment, dateExpected);
      const statusValue = String(status || "").trim().toLowerCase() === "paid" ? "paid" : "unpaid";

      const loan = await findOrCreateLoan(borrower.id, amountLoaned);
      await upsertAmortization({
        loanId: loan.id,
        borrowerNrc: borrower.nrc,
        monthLabel,
        dueDate,
        amountDue: amountLoaned,
        interestRatePct,
        expectedAmount,
        profit,
        status: statusValue,
      });

      if (statusValue === "paid") {
        await ensurePaymentRecorded({ loanId: loan.id, amount: expectedAmount, dueDate });
        result.paymentsRecorded += 1;
      }

      result.saved += 1;
    } catch (err) {
      result.errors.push(
        `${name}: ${err.response?.data?.errors?.[0]?.message || err.message}`
      );
    }
  }

  return result;
}
